/**
 * The MD analysis report: which sections exist, and the model a report is
 * built from. Pure functions (unit tested without a browser); drawing the
 * charts to pictures is ui/charting/renderChartImage.js, and writing the PDF or
 * PowerPoint file is integrations/mdReportExport.js.
 *
 * A report contains exactly what the user selects: compounds and sections.
 * Reports grow one section at a time: add an entry to MD_REPORT_SECTIONS (its
 * chart option builder and caption) and it appears in the dialog and in both
 * formats.
 */
import {
    hasInteractionDiagram, interactionDiagramCaption, interactionDiagramReportSvg,
    interactionDiagramSize,
} from './mdInteractionDiagram';
import {
    LIGAND_PROPERTIES_CAPTION, LIGAND_RMSF_CAPTION, RMSD_CHART_CAPTION, ligandPropertiesChartOption,
    ligandPropertiesSize, ligandPropertyRuns, ligandRmsfChartOption, ligandRmsfRuns,
    rmsdChartOption, rmsdRuns,
} from './mdCharts';
import {
    CONTACTS_HISTOGRAM_CAPTION, CONTACTS_TIMELINE_CAPTION, LIGAND_TORSIONS_CAPTION,
    SECONDARY_STRUCTURE_CAPTION, contactHistogramChartOption, contactRuns,
    contactTimelineChartOption, contactTimelineResidueCount, contactTimelineRuns,
    contactTimelineSize, hasLigandTorsions, ligandTorsionsReportSize, ligandTorsionsReportSvg,
    secondaryStructureChartOption, secondaryStructureRuns, secondaryStructureSize,
} from './mdStructureCharts';

/**
 * The sections a report can hold.
 * `available(compound)` says whether a compound has the data; `option(compound, opts)`
 * returns the ECharts option (opts: textColor, interactive); a section that is
 * not a chart gives `svg(compound)`, a standalone SVG string, instead; `caption` is
 * the definition text printed under the picture; `size(compound)` is the picture's
 * size in layout pixels (the default is a wide 1200 x 560 chart).
 */
export const MD_REPORT_SECTIONS = [
    {
        id: 'rmsd',
        title: 'Protein–ligand RMSD',
        caption: RMSD_CHART_CAPTION,
        available: (compound) => rmsdRuns(compound).length > 0,
        option: (compound, opts) => rmsdChartOption(compound, { ...opts, run: 'all' }),
    },
    {
        id: 'ligand-rmsf',
        title: 'Ligand RMSF',
        caption: LIGAND_RMSF_CAPTION,
        available: (compound) => ligandRmsfRuns(compound).length > 0,
        option: (compound, opts) => ligandRmsfChartOption(compound, { ...opts, run: 'mean' }),
        size: () => ({ width: 1200, height: 520 }),
    },
    {
        id: 'interaction-diagram',
        title: 'Ligand–protein contacts',
        caption: interactionDiagramCaption(),
        available: hasInteractionDiagram,
        svg: interactionDiagramReportSvg,
        size: interactionDiagramSize,
    },
    {
        id: 'contacts',
        title: 'Contacts by residue',
        caption: CONTACTS_HISTOGRAM_CAPTION,
        available: (compound) => contactRuns(compound).length > 0,
        option: (compound, opts) => contactHistogramChartOption(compound, { ...opts, run: 'mean' }),
        size: () => ({ width: 1200, height: 620 }),
    },
    {
        id: 'contacts-timeline',
        title: 'Contacts over time',
        caption: `${CONTACTS_TIMELINE_CAPTION} The first run is shown.`,
        available: (compound) => contactTimelineRuns(compound).length > 0,
        option: (compound, opts) => contactTimelineChartOption(compound, { ...opts, run: 'first' }),
        size: (compound) => contactTimelineSize(contactTimelineResidueCount(compound)),
    },
    {
        id: 'ligand-properties',
        title: 'Ligand properties',
        caption: LIGAND_PROPERTIES_CAPTION,
        available: (compound) => ligandPropertyRuns(compound).panels
            .some((p) => p.key !== 'ligand_rmsd_a'),
        option: (compound, opts) => ligandPropertiesChartOption(compound, { ...opts, run: 'all' }),
        size: (compound) => ligandPropertiesSize(ligandPropertyRuns(compound).panels.length),
    },
    {
        id: 'ligand-torsions',
        title: 'Ligand torsions',
        caption: LIGAND_TORSIONS_CAPTION,
        available: hasLigandTorsions,
        svg: ligandTorsionsReportSvg,
        size: ligandTorsionsReportSize,
    },
    {
        id: 'secondary-structure',
        title: 'Protein secondary structure',
        caption: `${SECONDARY_STRUCTURE_CAPTION} With several runs, the per-residue fractions are `
            + 'their mean and the map is the first run\'s.',
        available: (compound) => secondaryStructureRuns(compound).length > 0,
        option: (compound, opts) => secondaryStructureChartOption(compound, { ...opts, run: 'mean' }),
        size: () => secondaryStructureSize(false),
    },
];

export const MD_REPORT_FORMATS = [
    {
        key: 'pdf', label: 'PDF', extension: 'pdf', mime: 'application/pdf',
    },
    {
        key: 'pptx',
        label: 'PowerPoint (PPTX)',
        extension: 'pptx',
        mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    },
];

function fmt(value, digits = 2) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '';
    return Number(value).toFixed(digits);
}

function fmtPm(stat) {
    if (!stat || stat.mean === undefined) return '';
    return stat.sd !== undefined ? `${fmt(stat.mean)} ± ${fmt(stat.sd)}` : fmt(stat.mean);
}

/**
 * The compounds of a job's md/1 packet a report can include: simulated ones
 * that have data for at least one section.
 * @param {object} md - the md/1 packet
 * @returns {object[]}
 */
export function reportCompounds(md) {
    return (md?.compounds || []).filter((c) => c.status === 'succeeded'
        && MD_REPORT_SECTIONS.some((s) => s.available(c)));
}

/**
 * The sections at least one of the compounds has data for.
 * @param {object[]} compounds
 * @returns {{ id: string, title: string }[]}
 */
export function reportSectionsFor(compounds) {
    return MD_REPORT_SECTIONS
        .filter((s) => (compounds || []).some((c) => s.available(c)))
        .map(({ id, title }) => ({ id, title }));
}

/**
 * The job-level lines of a report: what was simulated, with what.
 * @param {object} md - the md/1 packet
 * @param {string} [method] - the job's method label (e.g. "GROMACS")
 * @returns {string[]}
 */
export function describeReportJob(md, method) {
    const params = md?.params || {};
    const prov = md?.provenance || {};
    const remd = params.replica_exchange;
    const lines = [];
    if (method) lines.push(`Engine: ${method}${prov.engine_version ? ` (${prov.engine_version})` : ''}`);
    if (params.length_ns !== undefined) {
        lines.push(remd
            ? `${params.length_ns} ns × ${remd.replicas} replicas`
            : `${params.length_ns} ns × ${params.copies} independent run${params.copies === 1 ? '' : 's'} at ${params.temperature_k} K`);
    }
    const m = md?.method;
    if (m?.protein_ff) lines.push(`Force fields: ${m.protein_ff} / ${m.ligand_ff} / ${m.water}`);
    if (m?.rmsd_reference) lines.push(`RMSD reference: ${m.rmsd_reference}`);
    if (prov.gpu) lines.push(`Computed on: ${prov.gpu}`);
    return lines;
}

/**
 * The summary lines of one compound.
 * @param {object} compound - a `compounds` entry
 * @returns {string[]}
 */
export function describeReportCompound(compound) {
    const agg = compound.aggregate || {};
    const lines = [];
    if (agg.pose) lines.push(`Pose: ${agg.pose}`);
    const lig = fmtPm(agg.ligand_rmsd_a);
    if (lig) lines.push(`Ligand RMSD: ${lig} Å`);
    const bb = fmtPm(agg.backbone_rmsd_a);
    if (bb) lines.push(`Backbone RMSD: ${bb} Å`);
    const runs = (compound.copies || []).length;
    if (runs) lines.push(`${runs} independent run${runs === 1 ? '' : 's'}`);
    return lines;
}

/**
 * What the export step needs: the title, job lines, and per selected compound
 * its summary and sections (each with the chart option to draw).
 * @param {object} p
 * @param {object} p.md - the md/1 packet
 * @param {string} p.title - the job's name
 * @param {string} [p.method]
 * @param {string[]} [p.compoundNames] - default: every reportable compound
 * @param {string[]} [p.sectionIds] - default: every section
 * @param {string} [p.generated] - a date string for the header
 * @param {string} [p.textColor] - text color of the pictures (dark, for paper)
 * @returns {object}
 */
export function buildReportModel({
    md, title, method, compoundNames, sectionIds, generated, textColor = '#222',
}) {
    const chosen = reportCompounds(md)
        .filter((c) => !compoundNames || compoundNames.includes(c.name));
    const sections = MD_REPORT_SECTIONS.filter((s) => !sectionIds || sectionIds.includes(s.id));
    return {
        title: 'MD analysis report',
        jobName: title,
        generated: generated || '',
        details: describeReportJob(md, method),
        compounds: chosen.map((c) => ({
            name: c.name,
            summary: describeReportCompound(c),
            sections: sections.filter((s) => s.available(c)).map((s) => ({
                id: s.id,
                title: s.title,
                caption: s.caption,
                option: s.option ? s.option(c, { textColor, interactive: false }) : undefined,
                svg: s.svg ? s.svg(c) : undefined,
                size: s.size ? s.size(c) : undefined,
            })),
        })).filter((c) => c.sections.length > 0),
    };
}

/**
 * A file name for the report: the job name made safe, plus the extension.
 * @param {string} title
 * @param {string} extension
 * @returns {string}
 */
export function reportFileName(title, extension) {
    const safe = String(title || 'md').replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^_+|_+$/g, '');
    return `${safe || 'md'}_analysis_report.${extension}`;
}
